/**
 * StickyCtaBar — 하단 고정 CTA 바 (목업 `.cta`).
 *
 * 데스크톱 패널·모바일 바텀시트·상세페이지가 공유하는 순수 프레젠테이션 컴포넌트.
 * 전화(아웃라인) + 카톡(accent 채움) 두 버튼.
 *
 * 정책:
 *   - 전화번호는 phone prop(resolveContactPhone 결과). 미지정 시 대표번호 1533-8124 (tel:15338124). (v2 2026-09-21)
 *   - 카톡 링크는 반드시 referrerPolicy="unsafe-url" + rel="noopener" 유지 (유입경로 정책).
 *   - referrerPolicy 를 절대 noreferrer 로 바꾸지 않는다.
 */

import React from 'react';
import {
  INQUIRY_KAKAO_CHANNEL_URL,
  handleKakaoChatClick,
} from '../../../domain/inquiry/PHONE';
import { DEFAULT_CONTACT_PHONE, type ContactPhone } from '../../../domain/inquiry/resolveContactPhone';

export interface StickyCtaBarProps {
  className?: string;
  /**
   * inline=true 면 컨테이너 기준(relative w-full)으로 배치한다.
   * 데스크톱 우측 패널(440px)에서 화면중앙 fixed 로 뜨는 문제를 피하기 위함.
   * 기본 false — 모바일 하단 고정(fixed) 유지.
   */
  inline?: boolean;
  /** 전화번호 — resolveContactPhone(property). 미지정 시 대표번호 */
  phone?: Pick<ContactPhone, 'display' | 'tel'>;
}

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width={17} height={17} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7A2 2 0 0 1 22 16.9z" />
  </svg>
);

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" width={17} height={17} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M12 3C6.5 3 2 6.6 2 11c0 2.8 1.9 5.3 4.7 6.7-.2.7-.7 2.6-.8 3-.1.5.2.5.4.4.2-.1 2.6-1.8 3.6-2.5.7.1 1.4.2 2.1.2 5.5 0 10-3.6 10-8S17.5 3 12 3z" />
  </svg>
);

export function StickyCtaBar({ className = '', inline = false, phone = DEFAULT_CONTACT_PHONE }: StickyCtaBarProps) {
  const position = inline
    ? 'relative w-full'
    : 'fixed bottom-0 left-1/2 w-full max-w-[440px] -translate-x-1/2';
  return (
    <nav
      className={`${position} z-40 flex gap-2 border-t border-ca-line bg-frame px-3 py-2.5 ${className}`}
      style={{ paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))' }}
    >
      <a
        href={phone.tel}
        aria-label={`전화 ${phone.display}`}
        className="flex flex-1 items-center justify-center gap-2 rounded-ca-xl border border-ca-line-strong bg-frame text-[14.5px] font-extrabold text-ink"
        style={{ height: 48 }}
      >
        <PhoneIcon />
        <span className="num">전화 {phone.display}</span>
      </a>
      <a
        href={INQUIRY_KAKAO_CHANNEL_URL}
        target="_blank"
        rel="noopener"
        referrerPolicy="unsafe-url"
        onClick={handleKakaoChatClick}
        aria-label="카톡 상담"
        className="flex flex-[1.3] items-center justify-center gap-2 rounded-ca-xl bg-accent text-[14.5px] font-extrabold text-white"
        style={{ height: 48 }}
      >
        <ChatIcon />
        <span>카톡 상담</span>
      </a>
    </nav>
  );
}

export default StickyCtaBar;
